import { makeStyles, createStyles, Grid, Typography, Button, CircularProgress } from "@material-ui/core";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Theme } from "react-select";
import { IKImage } from "imagekitio-react";
import fallback_img from "../../../../../images/fallback_item.png";
import {
  selectComponentDimensions,
  selectGroupItemsEntities,
  selectLandingFromGroup,
} from "../../../../../redux/slices/landingSlice";
import { PFXUpsellingComponent } from "../../../../../models/Interfaces";
import { selectIsMobile } from "../../../../../redux/slices/cleverlynkSlice";
import useLandingFunctions from "../../../../../hooks/useLandingFunctions";
import { RootState } from "../../../../../redux/rootReducer";
import { selectItemById } from "../../../../../redux/slices/templateSlice";
import { BUTTON_FONTSIZE_BASE, BUTTON_PADD_BASE, MARGIN_BASE, TITLE_FONTSIZE_BASE } from "../../../../../pages/Landing";

interface IExpanded {
  data: PFXUpsellingComponent;
  loading: boolean;
  id: string;
}

const Expanded = ({ data, id, loading }: IExpanded) => {
  const { width } = useSelector(selectComponentDimensions);
  const { handleGoToItem } = useLandingFunctions();

  const isMobile = useSelector(selectIsMobile);

  const groupItems = useSelector(selectGroupItemsEntities);
  const landingFromGroup = useSelector(selectLandingFromGroup);

  const item = useSelector((state: RootState) => {
    if (landingFromGroup)
      return groupItems[(data?.productIds ?? [{ elementId: "", templateID: "" }])[0]?.elementId ?? ""];
    else return selectItemById(state, (data?.productIds ?? [{ elementId: "", templateID: "" }])[0]?.elementId ?? "");
  });

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      container: {
        width,
      },
      root: {
        // minHeight: height,
        backgroundColor: data?.backgroundColor ?? "#f9f9f9",
        display: "flex",
        alignContent: "center",
        justifyContent: "center",
        padding: `${(data?.margins?.y ?? 2) * MARGIN_BASE}px ${(data?.margins?.x ?? 2) * MARGIN_BASE}px`,
      },
      photoGridItem: {
        maxWidth: isMobile ? "95%" : "40%",
        flexBasis: isMobile ? "95%" : "40%",
        paddingTop: 15,
        paddingBottom: 15,
      },
      infoGridItem: {
        maxWidth: isMobile ? "100%" : "60%",
        flexBasis: isMobile ? "100%" : "60%",
        padding: isMobile ? "" : 15,
      },
      card: {
        width: isMobile ? 350 : "90%",
        margin: "0 auto",
        boxShadow: "1pt 1pt 4px -2px #000000b5",
        backgroundColor: "white",
        borderRadius: 9,
        marginTop: 10,
        marginBottom: 10,
        padding: 10,
        paddingTop: isMobile ? 0 : 10,
      },
      button: {
        backgroundColor: data.button?.backgroundColor,
        "&:hover": {
          backgroundColor: `${data.button?.backgroundColor}8c`,
        },
        color: data.button?.text?.color ?? "#1a1a1a",
        borderRadius: data.button?.borderRadius,
        padding: (data.button?.sizeMultiplier ?? 1) * BUTTON_PADD_BASE,
        fontSize: `${data.button?.text?.fontSizeMultiplier ?? 1 * BUTTON_FONTSIZE_BASE}rem`,
        fontWeight: data.button?.text?.fontWeight as any,
        fontFamily: data.button?.text?.fontFamily,
        height: 1,
        textTransform: "none",
        boxShadow: "none",
        marginTop: isMobile ? 0 : 15,
      },
      image: {
        maxHeight: 350,
        maxWidth: 350,
        width: "100%",
        borderRadius: isMobile ? "9px 9px 0px 0px" : 9,
      },
      title: {
        fontSize: `${data.title?.fontSizeMultiplier ?? 1 * TITLE_FONTSIZE_BASE}rem`,
        color: data.title?.color ?? "#1a1a1a",
        fontWeight: data.title?.fontWeight as any,
        fontFamily: data.title?.fontFamily,
      },
    })
  );

  const classes = useStyles();

  const imagePath = useMemo(() => {
    if (!item || !item?.images) return undefined;
    return item?.images && item.images.length > 0 && item.images[0]
      ? item.images[0].split("cleverlynk-imgs.s3.amazonaws.com")[1]
      : undefined;
  }, [item]);

  return (
    <div id={`landing-component-${id}`} className={classes.root}>
      <Grid item container className={classes.container}>
        <Grid container justifyContent="center" className={classes.card} xs={isMobile ? 12 : 10}>
          {loading ? (
            <Grid item container justifyContent="center" alignItems="center" style={{ height: 250 }}>
              <CircularProgress size={50} />
            </Grid>
          ) : (
            <React.Fragment>
              <Grid item className={classes.photoGridItem} container justifyContent="center">
                {imagePath ? (
                  <IKImage
                    id={`expanded-${item?.id}-${imagePath}`}
                    key={`expanded-${item?.id}-${imagePath}`}
                    className={classes.image}
                    style={{
                      position: "relative",
                    }}
                    path={imagePath}
                    transformation={[
                      {
                        height: 350,
                        width: 350,
                      },
                    ]}
                    loading="lazy"
                    lqip={{ active: true, quality: 20, blur: 10 }}
                  />
                ) : (
                  <img
                    src={fallback_img}
                    style={{
                      margin: "0 auto",
                    }}
                    className={classes.image}
                    alt={"fallback item"}
                  />
                )}
              </Grid>
              <Grid item container className={classes.infoGridItem} direction="column" justifyContent="center">
                <Grid item>
                  <Typography variant="body1" className={classes.title}>
                    {item?.name ?? "Selecciona un producto de tu catálogo"}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption" style={{ color: "#969696" }} paragraph>
                    {(item?.description ?? "Da visibilidad a tu producto más destacado").substr(0, 180)}
                  </Typography>
                </Grid>
                <Grid item container justifyContent={!isMobile ? "flex-start" : "center"}>
                  <Button
                    className={classes.button}
                    variant="contained"
                    onClick={() => (!!item ? handleGoToItem(item, undefined, undefined) : null)}>
                    Ver producto
                  </Button>
                </Grid>
              </Grid>
            </React.Fragment>
          )}
        </Grid>
      </Grid>
    </div>
  );
};
export default Expanded;
